import * as moment from 'moment';
import { IWatrRequest } from '@nkr/models/index';
import { FileDocumentType } from '@nkr/enums/document-type';
import { isHourly, isSelfEmployed } from '../utils/employment-type.utils';
import { Button, FormControl, InputGroup } from 'react-bootstrap';
import { SsnDisplay } from '../ssn-display';
import WeekByWeekBreakdown from './week-by-week-breakdown';
import { computeWageModel, hasAnyWageData } from './wage-computation';
import { formatMoneyInput } from './money-input';
import * as React from 'react';
import { useState } from 'react';

type WageSummaryMode = 'readOnly' | 'verify';

const usesUsTaxIdentity = (data: IWatrRequest): boolean => data.filesTaxesInUs !== false;

interface IProps {
    data: IWatrRequest;
    mode: WageSummaryMode;
    wageReimbursementAmount: number;
    showWeekByWeekBreakdown?: boolean;
    draftOtherAmounts?: Record<number, number>;
    onOtherAmountChange?: (weekRequested: number, otherAmount: number) => void;
    onOverrideFocus?: (e: React.FocusEvent<any>) => void;
    onOverrideBlur?: (e: React.FocusEvent<any>) => void;
    onOverrideChange?: (e: React.FormEvent<any>) => void;
    showWageVerificationDocuments?: boolean;
    onDownloadWageVerificationDocument?: (docType: FileDocumentType) => void;
    showWageAmountPill?: boolean;
    wageAmountPillAmount?: number;
}

const WageSummaryShared = (props: IProps) => {
    const [overrideFocused, setOverrideFocused] = useState(false);
    const isVerifyMode = props.mode === 'verify';
    const showWeekByWeekBreakdown = props.showWeekByWeekBreakdown !== false;
    const isSelfEmployedValue = isSelfEmployed(props.data.employmentType);
    const isNotSelfEmployed = !isSelfEmployedValue;
    const isHourlyValue = isHourly(props.data.employmentType);
    const hasTaxReturnData = (props.data.calculatedWeeklyPayByYear > 0 || props.data.incomeIrsYearGross > 0);
    const showTaxReturnColumn = !isHourlyValue || hasTaxReturnData;

    const hasData = hasAnyWageData(props.data, props.wageReimbursementAmount);

    const panelClass = isVerifyMode ? 'panel wage-summary-panel' : 'panel';
    const panelHeader = isVerifyMode ? (
        <div className="panel-header">
            <span className="panel-header-title">Wage summary</span>
            <span className="panel-header-note">Review the weekly amount before approving</span>
        </div>
    ) : (
        <div className="panel-header">Wage Summary</div>
    );

    if (!hasData) {
        return (
            <div className={panelClass}>
                {panelHeader}
                <div className="panel-body wage-summary-empty">
                    <div className="empty-state">
                        <div className="empty-state-title">Awaiting donor wage submission</div>
                        <div className="empty-state-subtitle">
                            Tax, paystub, and reimbursement details will appear here once the donor completes their submission.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const stubStartMoment = moment(props.data.incomePayStubStartDate);
    const isStubStartValid = !!props.data.incomePayStubStartDate && stubStartMoment.isValid() && stubStartMoment.year() > 1;
    const stubEndMoment = moment(props.data.incomePayStubEndDate);
    const isStubEndValid = !!props.data.incomePayStubEndDate && stubEndMoment.isValid() && stubEndMoment.year() > 1;

    const model = computeWageModel(props.data, props.wageReimbursementAmount, props.draftOtherAmounts);

    const hasPayStubFile = props.data.payStubFile && props.data.requireSelfEmployedAffirmation == false;
    const hasTaxFile = !!props.data.taxDocumentFile;
    const hasSelfEmployedAffidavit = !!props.data.selfEmployedAffirmationFile;
    const hasWageVerificationDocs =
        (!isHourlyValue && hasTaxFile) || hasPayStubFile || hasSelfEmployedAffidavit;
    const showWageVerificationDocuments = props.showWageVerificationDocuments === true;
    const summaryColumnCount = (showTaxReturnColumn ? 1 : 0) + (isNotSelfEmployed ? 1 : 0) + 1;

    const renderWageVerificationDocuments = () => {
        if (!showWageVerificationDocuments) {
            return null;
        }

        const onDownload = props.onDownloadWageVerificationDocument;

        return (
            <div className='summary-section wage-summary-verification-docs'>
                <div className='summary-section-title'>Documents</div>
                <div className='doc-actions-row doc-actions-row-compact'>
                    {!isHourlyValue && hasTaxFile && (
                        <Button
                            bsStyle='default'
                            bsSize='sm'
                            disabled={!onDownload}
                            onClick={() => onDownload?.(FileDocumentType.Tax)}>
                            Tax return
                        </Button>
                    )}
                    {hasPayStubFile && (
                        <Button
                            bsStyle='default'
                            bsSize='sm'
                            disabled={!onDownload}
                            onClick={() => onDownload?.(FileDocumentType.PayStub)}>
                            Pay stub
                        </Button>
                    )}
                    {hasSelfEmployedAffidavit && (
                        <Button
                            bsStyle='default'
                            bsSize='sm'
                            disabled={!onDownload}
                            onClick={() => onDownload?.(FileDocumentType.DsSelfEmployedAffidavit)}>
                            Self-employed affidavit
                        </Button>
                    )}
                    {!hasWageVerificationDocs && (
                        <span className='wage-docs-empty'>No wage verification documents on file.</span>
                    )}
                    {props.showWageAmountPill && props.wageAmountPillAmount != null && (
                        <div className='data-item wage-docs-amount'>
                            <div className='lbl'>Wage Amount</div>
                            <div className='val'>
                                ${props.wageAmountPillAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={panelClass}>
            {panelHeader}
            <div className="panel-body">
                <div className="final-bar">
                    <div className="item"><div className="lbl">Final Reimbursement Total</div><div className="val">${model.finalReimbursementTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                    <div className="sep" />
                    <div className="item"><div className="lbl">Weeks Included</div><div className="val">{model.weeksIncluded}</div></div>
                    <div className="sep" />
                    <div className="item"><div className="lbl">Effective Weekly Amount</div><div className="val">${model.adjustedBaseForWeeks.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                    {model.incomePercentCovered > 0 && (
                        <>
                            <div className="sep" />
                            <div className="item"><div className="lbl">Income Covered</div><div className="val">{model.incomePercentCovered}%</div></div>
                        </>
                    )}
                </div>
                <div className={`summary-columns summary-columns-${summaryColumnCount}`}>
                    {showTaxReturnColumn && (
                        <div className="summary-section summary-col">
                            <div className="summary-section-title">Tax Information</div>
                            <div className="data-grid summary-grid-two">
                                <div className="data-item">
                                    <div className="lbl">Tax Year</div>
                                    <div className="val">{props.data.incomeIrsYear}</div>
                                </div>
                                <div className="data-item">
                                    <div className="lbl">Tax Gross</div>
                                    <div className="val">${Number(props.data.incomeIrsYearGross || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                                <div className="data-item align-bottom">
                                    <div className="lbl">Tax-Derived Weekly</div>
                                    <div className="val accent">${Number(props.data.calculatedWeeklyPayByYear || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                                <div className="data-item" aria-hidden="true" />
                            </div>
                        </div>
                    )}
                    {isNotSelfEmployed && (
                        <div className="summary-section summary-col">
                            <div className="summary-section-title">Paystub Information</div>
                            <div className="date-pair date-pair-three">
                                <div className="data-item">
                                    <div className="lbl">Stub Start</div>
                                    <div className="val">{isStubStartValid ? stubStartMoment.format('MM/DD/YYYY') : '--'}</div>
                                </div>
                                <div className="data-item">
                                    <div className="lbl">Stub End</div>
                                    <div className="val">{isStubEndValid ? stubEndMoment.format('MM/DD/YYYY') : '--'}</div>
                                </div>
                            </div>
                            <div className="data-grid summary-grid-compact">
                                <div className="data-item">
                                    <div className="lbl">Est. Workdays</div>
                                    <div className="val">{props.data.calculatedBusinessDaysInPayStub}</div>
                                </div>
                                <div className="data-item">
                                    <div className="lbl">{isHourlyValue && (props.data.paystubs || []).length > 1 ? 'Combined Gross' : 'Stub Gross'}</div>
                                    <div className="val">${Number(props.data.incomePayStubGross || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                                <div className="data-item">
                                    <div className="lbl">Paystub-Derived Weekly</div>
                                    <div className="val accent">${Number(props.data.calculatedWeeklyPayByPayStub || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="summary-section summary-col summary-col-identity">
                        <div className="summary-section-title">Identity</div>
                        <div className="data-grid summary-grid-compact">
                            <div className="data-item">
                                <div className="lbl">{usesUsTaxIdentity(props.data) ? 'SSN' : 'TID'}</div>
                                <div className="ssn-row">
                                    <SsnDisplay ssnDecrypted={props.data.ssnDecrypted} fallback="N/A" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {renderWageVerificationDocuments()}
            </div>

            {showWeekByWeekBreakdown && (
                <WeekByWeekBreakdown
                    data={props.data}
                    allowEditOtherAmount={isVerifyMode}
                    draftOtherAmounts={props.draftOtherAmounts}
                    onOtherAmountChange={isVerifyMode ? props.onOtherAmountChange : undefined}
                    overrideWeeklyAmount={model.effectiveWeeklyOverride}
                />
            )}

            {isVerifyMode && (
                <div className="panel override-panel">
                    <div className="panel-body">
                        <div className="override-explainer">
                            Weekly base override (applied before each week's Other Amount adjustment)
                        </div>
                        <div className={`override-bar ${model.isWeeklyOverrideActive ? 'is-active' : ''}`}>
                            <label>Weekly Amount Override</label>
                            <InputGroup className="input-group">
                                <InputGroup.Addon>$</InputGroup.Addon>
                                <FormControl
                                    id="wageReimbursementAmount"
                                    name="wageReimbursementAmount"
                                    type="text"
                                    inputMode="decimal"
                                    value={overrideFocused
                                        ? (props.wageReimbursementAmount ? String(props.wageReimbursementAmount) : '')
                                        : formatMoneyInput(props.wageReimbursementAmount || 0)}
                                    onFocus={(e: React.FocusEvent<any>) => {
                                        setOverrideFocused(true);
                                        props.onOverrideFocus?.(e as React.FocusEvent<HTMLInputElement & FormControl>);
                                    }}
                                    onBlur={(e: React.FocusEvent<any>) => {
                                        setOverrideFocused(false);
                                        props.onOverrideBlur?.(e as React.FocusEvent<HTMLInputElement & FormControl>);
                                    }}
                                    onChange={props.onOverrideChange as any}
                                />
                            </InputGroup>
                            <span className="note">{model.isWeeklyOverrideActive ? 'Override active' : 'Using base weekly amount'}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WageSummaryShared;
